import React from 'react';
import { connect } from 'react-redux';
import { mapDispatchToProps, mapStateToProps, Props, SortAndFilterContentVariant } from 'component/page/search/SortAndFilter/SortAndFilterContent.selectors';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sort } from 'gql/autogen/Sort';
import { RangeBound } from 'gql/autogen/RangeBound';
import { CategoryId } from 'core/type/Category.types';
import { searchInitialState } from 'component/page/search/store/search';
import { getSortAndFilterQueryString } from 'component/page/search/SortAndFilter/sortAndFilterQuery.utils';
import {
    getCategoryItems,
    getCategoryWithSubcategoryItems,
    getDurationItems,
    getFilterStateByRange,
    getFilterStateByValue,
    getFilterStateForOneValue,
    getBookingFeatureItems,
    getPriceFilter,
    getStarRatingItems,
    getSubcategoryItems,
} from 'component/page/search/SortAndFilter/Filter/filter.utils';
import SortCard from 'component/page/search/SortAndFilter/Sort/SortCard';
import FilterContainer from 'component/page/search/SortAndFilter/Filter/FilterContainer';
import CategoryFilter from 'component/page/search/SortAndFilter/CategoryFilter/CategoryFilter';
import CategoryWithSubcategoryFilter from 'component/page/search/SortAndFilter/CategoryWithSubcategoryFilter/CategoryWithSubcategoryFilter';
import { FilterCode } from 'gql/autogen/FilterCode';
import SelectedFilter from 'component/page/search/SortAndFilter/SelectedFilter/SelectedFilter';
import { FilterState } from 'component/common/Filter/Filter.types';
import { FeatureName } from 'core/constant/FeatureName';
import Feature from 'component/common/Experiment/Feature';
import { getSubcategoryIdsFromCategoryId, onSubcategoryFilterChange } from 'component/page/search/SortAndFilter/CategoryWithSubcategoryFilter/Subcategory.utils';
import { SubcategoryId } from 'core/type/Subcategory.types';
import PopularFilter from 'component/page/search/SortAndFilter/Filter/PopularFilter';

const SortAndFilterContent: React.FC<Props> = (props) => {
    const locationSearch = useLocation();
    const navigation = useNavigate();
    const { sort, filter, filterMatrix, sortMatrix, visible, variant, isLoading, cms } = props;
    const [currentSort, setCurrentSort] = React.useState(sort);
    const [currentFilter, setCurrentFilters] = React.useState(filter);
    React.useEffect(() => {
        setCurrentSort(sort);
        setCurrentFilters(filter);
    }, [filter, sort, visible]);
    const isSidebar = variant === SortAndFilterContentVariant.Sidebar;
    const isFilterBottomSheet = variant === SortAndFilterContentVariant.CategoryBottomSheet;

    const categoryWithSubcategoryFilter = getCategoryWithSubcategoryItems(cms, currentFilter.valueFilterMatrix, filterMatrix?.valueFilterMatrix);
    const subcategoryFilter = getSubcategoryItems(cms, currentFilter.valueFilterMatrix, filterMatrix?.valueFilterMatrix);

    const handleSortAndFilterChange = (sortState: Sort, filterState: FilterState, isDeleteKeyword = false) => {
        if (!visible) return;

        setCurrentFilters(filterState);
        setCurrentSort(sortState);

        props.onFilterStateChange && props.onFilterStateChange(filterState);
        props.onSortStateChange && props.onSortStateChange(sortState);

        if (isSidebar) {
            const query = getSortAndFilterQueryString(locationSearch.search, sortState, filterState, true, isDeleteKeyword);
            navigation(`?${query}`, { replace: props.isFilterWithoutUpdatingHistory });
        }
    };

    const onClearSortAndFilter = () => {
        handleSortAndFilterChange(currentSort, searchInitialState.filter, !props.isGlobalKeywordSearchPage);
    };

    const onValueFilterChange = (code: FilterCode, values: string[], selected: boolean) => {
        const state = getFilterStateByValue(code, values, selected, currentFilter);
        handleSortAndFilterChange(currentSort, state);
    };

    const onCategoryFilterChange = (categories: CategoryId[], index: number, count: number) => {
        const filterState = getFilterStateForOneValue(FilterCode.Category, categories, currentFilter);
        handleSortAndFilterChange(currentSort, filterState);
        const categoryValue = categories.length > 0 ? categories[0] : CategoryId.All;
        props.onCategoryFilterChange(categoryValue, index, count); // only tracking
    };

    const onCategoryWithSubcategoryFilterChange = (value: CategoryId, selected: boolean) => {
        const state = getFilterStateByValue(FilterCode.Category, [value], selected, currentFilter);
        const subcategories = getSubcategoryIdsFromCategoryId(value, subcategoryFilter);
        const stateWithSubcategoryUpdate = getFilterStateByValue(FilterCode.SubCategory, subcategories, false, state);
        handleSortAndFilterChange(currentSort, stateWithSubcategoryUpdate);
    };

    const onSubcategoryFilterChangeHandler = (value: SubcategoryId, selected: boolean) => {
        const newFilterState = onSubcategoryFilterChange(currentFilter, subcategoryFilter, value, selected);
        handleSortAndFilterChange(currentSort, newFilterState);
    };

    const onClearCategoryFilter = () => {
        const filterState = getFilterStateForOneValue(FilterCode.Category, [], currentFilter);
        const stateWithSubcategoryUpdate = getFilterStateForOneValue(FilterCode.SubCategory, [], filterState);
        handleSortAndFilterChange(currentSort, stateWithSubcategoryUpdate);
    };

    const onRangeFilterChange = (code: FilterCode, lowerBound: RangeBound, upperBound: RangeBound, selected: boolean) => {
        const filterState = getFilterStateByRange(code, lowerBound, upperBound, selected, currentFilter, filterMatrix?.rangeFilterMatrix);
        handleSortAndFilterChange(currentSort, filterState);
    };

    const onSortSelectionChange = (newSort: Sort, index: number) => {
        handleSortAndFilterChange(newSort, currentFilter);
        props.onSortSelectionChange(newSort, index);
    };

    const priceFilter = getPriceFilter(cms, currentFilter.rangeFilterMatrix, filterMatrix?.rangeFilterMatrix);
    const starRatingFilter = getStarRatingItems(cms, currentFilter.rangeFilterMatrix, filterMatrix?.rangeFilterMatrix);
    const durationFilter = getDurationItems(cms, currentFilter.valueFilterMatrix, filterMatrix?.valueFilterMatrix);
    const categoryFilter = getCategoryItems(cms, currentFilter.valueFilterMatrix, filterMatrix?.valueFilterMatrix);
    const bookingFeatureFilter = getBookingFeatureItems(cms, currentFilter.valueFilterMatrix, filterMatrix?.valueFilterMatrix);

    return (
        <>
            {!isFilterBottomSheet && (
                <>
                    <SelectedFilter
                        cms={props.cms}
                        priceFilter={priceFilter}
                        categoryFilter={categoryFilter}
                        categoryWithSubcategoryFilter={categoryWithSubcategoryFilter}
                        subcategoryFilter={subcategoryFilter}
                        starRatingFilter={starRatingFilter}
                        durationFilter={durationFilter}
                        bookingFeatureFilter={bookingFeatureFilter}
                        onCategoriesChange={onCategoryFilterChange}
                        onCategoryWithSubcategoryChange={onCategoryWithSubcategoryFilterChange}
                        onSubcategoryFilterChange={onSubcategoryFilterChangeHandler}
                        onValueFilterChange={onValueFilterChange}
                        onRangeFilterChange={onRangeFilterChange}
                        onClearSortAndFilter={onClearSortAndFilter}
                        isSidebar={isSidebar}
                    />
                    <SortCard cms={cms} sortMatrix={sortMatrix} currentSort={currentSort} visible={visible} onChangeHandler={onSortSelectionChange} />
                </>
            )}
            <Feature.On name={FeatureName.SubcategoryFilter}>
                <PopularFilter title={props.cms[350451]} variant={variant} onSubcategoryFilterChange={onSubcategoryFilterChangeHandler} subcategoryFilter={subcategoryFilter} />
            </Feature.On>
            {!isFilterBottomSheet && (
                <FilterContainer
                    visible={visible}
                    onValueFilterChange={onValueFilterChange}
                    onRangeFilterChange={onRangeFilterChange}
                    cms={cms}
                    starRatingFilter={starRatingFilter}
                    priceFilter={priceFilter}
                    durationFilter={durationFilter}
                    bookingFeatureFilter={bookingFeatureFilter}
                    isSidebar={isSidebar}
                    isLoading={isLoading}
                />
            )}
            <Feature.On name={FeatureName.SubcategoryFilter}>
                <CategoryWithSubcategoryFilter
                    cms={props.cms}
                    items={categoryWithSubcategoryFilter}
                    onCategoriesChange={onCategoryWithSubcategoryFilterChange}
                    onSubcategoriesChange={onSubcategoryFilterChangeHandler}
                    onClearCategoryFilter={onClearCategoryFilter}
                    variant={variant}
                />
            </Feature.On>
            <Feature.Off name={FeatureName.SubcategoryFilter}>
                <CategoryFilter cms={props.cms} items={categoryFilter} onCategoriesChange={onCategoryFilterChange} />
            </Feature.Off>
        </>
    );
};

SortAndFilterContent.displayName = 'SortAndFilterContent';

export default connect(mapStateToProps, mapDispatchToProps)(SortAndFilterContent);
